/**
 * DiscoveryFlowPanel - Three-panel view showing discovery process, work, and compliance metadata
 *
 * Left: Process flow with current step highlighted
 * Middle: Current work being done (documents, extractions)
 * Right: File metadata with model attribution, regional location, and FINMA compliance
 */

import { useState } from "react";
import {
  Shield,
  MapPin,
  Brain,
  FileText,
  Check,
  ChevronRight,
  Fingerprint,
  BarChart3,
  Route,
  FileOutput,
  ShieldCheck,
  Lock,
  Globe,
  AlertTriangle,
  Info,
  ExternalLink,
  Zap,
  Sparkles,
  Coins,
  Settings,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type {
  SourceDocument,
  CensusFirstPipelineSummary,
} from "@shared/discovery-types";

// Model tier definitions
export type ModelTier = "standard" | "budget" | "power" | "custom";

// Available AI models for extraction - 4 tiers as requested
export const AVAILABLE_MODELS = [
  {
    id: "gemini-3-flash-preview",
    name: "Standard",
    fullName: "Gemini 3.0 Flash",
    description: "Best balance of quality and cost (recommended)",
    tier: "standard" as ModelTier,
    costPerMInputTokens: 0.5,
    costPerMOutputTokens: 3.0,
    icon: "zap", // fast, efficient
  },
  {
    id: "gemini-2.5-flash-lite",
    name: "Budget",
    fullName: "Gemini 2.5 Flash-Lite",
    description: "Lowest cost, good for simple documents",
    tier: "budget" as ModelTier,
    costPerMInputTokens: 0.075,
    costPerMOutputTokens: 0.3,
    icon: "coins", // cost-effective
  },
  {
    id: "gemini-3-pro-preview",
    name: "Power",
    fullName: "Gemini 3.0 Pro",
    description: "Maximum quality for complex documents",
    tier: "power" as ModelTier,
    costPerMInputTokens: 2.0,
    costPerMOutputTokens: 12.0,
    icon: "sparkles", // premium
  },
] as const;

// Custom model option (user-configured from AI settings)
export const CUSTOM_MODEL_OPTION = {
  id: "custom",
  name: "Custom",
  fullName: "User-Configured Model",
  description: "Use model from AI Settings",
  tier: "custom" as ModelTier,
  settingsUrl: "/ai-settings",
  icon: "settings",
} as const;

export type ModelId = (typeof AVAILABLE_MODELS)[number]["id"] | "custom";

// Helper to get model by ID
export function getModelById(id: ModelId) {
  if (id === "custom") return CUSTOM_MODEL_OPTION;
  return AVAILABLE_MODELS.find(m => m.id === id) || AVAILABLE_MODELS[0];
}

// ============================================================================
// TYPES
// ============================================================================

interface ProcessPhase {
  id: string;
  label: string;
  description: string;
  status: "pending" | "active" | "completed";
  icon: React.ComponentType<{ className?: string }>;
}

type ComplianceStatus =
  | "finma_compliant"
  | "non_finma_acknowledged"
  | "finma_not_required";

interface ExtractionMetadata {
  modelUsed: string;
  modelVersion: string;
  region: string;
  regionCode: string;
  datacenterLocation: string;
  processingTimeMs?: number;
  tokensUsed?: number;
  finmaCompliant: boolean;
  finmaChecks: FinmaCheck[];
  complianceStatus?: ComplianceStatus;
  dataResidency: "switzerland" | "eu" | "unknown";
  encryptionStatus: "at-rest" | "in-transit" | "both" | "none";
}

interface FinmaCheck {
  id: string;
  label: string;
  description: string;
  status: "pass" | "fail" | "warning" | "not_applicable";
}

interface DiscoveryFlowPanelProps {
  documents: SourceDocument[];
  currentPhase?: string;
  extractionMetadata?: Partial<ExtractionMetadata>;
  isProcessing?: boolean;
  censusFirstResult?: CensusFirstPipelineSummary;
  selectedModel?: ModelId;
  onModelChange?: (modelId: ModelId) => void;
  canChangeModel?: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const CENSUS_FIRST_PHASES: ProcessPhase[] = [
  {
    id: "detect",
    label: "Detect",
    description: "Document fingerprinting",
    status: "pending",
    icon: Fingerprint,
  },
  {
    id: "census",
    label: "Census",
    description: "Corpus understanding",
    status: "pending",
    icon: BarChart3,
  },
  {
    id: "strategy",
    label: "Strategy",
    description: "Extraction planning",
    status: "pending",
    icon: Route,
  },
  {
    id: "extract",
    label: "Extract",
    description: "Source-attributed extraction",
    status: "pending",
    icon: FileOutput,
  },
  {
    id: "attribute",
    label: "Attribute",
    description: "Validation & linking",
    status: "pending",
    icon: ShieldCheck,
  },
  {
    id: "place",
    label: "Place",
    description: "Result routing",
    status: "pending",
    icon: MapPin,
  },
];

const DEFAULT_FINMA_CHECKS: FinmaCheck[] = [
  {
    id: "data_residency",
    label: "Data Residency",
    description:
      "Financial data processed within Switzerland or approved EU regions",
    status: "pass",
  },
  {
    id: "encryption",
    label: "Encryption",
    description: "Data encrypted at rest and in transit",
    status: "pass",
  },
  {
    id: "audit_trail",
    label: "Audit Trail",
    description: "All processing steps logged with timestamps",
    status: "pass",
  },
  {
    id: "data_minimization",
    label: "Data Minimization",
    description: "Only necessary data extracted and stored",
    status: "pass",
  },
  {
    id: "access_control",
    label: "Access Control",
    description: "User-level access restrictions enforced",
    status: "pass",
  },
  {
    id: "model_governance",
    label: "Model Governance",
    description: "AI models comply with Swiss AI guidelines",
    status: "pass",
  },
];

// ============================================================================
// COMPONENT
// ============================================================================

export function DiscoveryFlowPanel({
  documents,
  currentPhase,
  extractionMetadata,
  isProcessing = false,
  censusFirstResult,
  selectedModel = "gemini-3-flash-preview",
  onModelChange,
  canChangeModel = false,
}: DiscoveryFlowPanelProps) {
  const [selectedDocument, setSelectedDocument] =
    useState<SourceDocument | null>(null);

  // Get the current model info
  const currentModel = getModelById(selectedModel);

  // Get tier icon component
  const getTierIcon = (tier: ModelTier) => {
    switch (tier) {
      case "standard":
        return Zap;
      case "budget":
        return Coins;
      case "power":
        return Sparkles;
      case "custom":
        return Settings;
      default:
        return Zap;
    }
  };

  // Get tier color class
  const getTierColor = (tier: ModelTier) => {
    switch (tier) {
      case "standard":
        return "text-primary";
      case "budget":
        return "text-success";
      case "power":
        return "text-warning";
      case "custom":
        return "text-muted-foreground";
      default:
        return "text-primary";
    }
  };

  // Build phases with status
  const phases = CENSUS_FIRST_PHASES.map(phase => {
    let status: ProcessPhase["status"] = "pending";

    if (currentPhase) {
      const phaseIndex = CENSUS_FIRST_PHASES.findIndex(p => p.id === phase.id);
      const currentIndex = CENSUS_FIRST_PHASES.findIndex(p =>
        currentPhase.toLowerCase().includes(p.id)
      );

      if (currentIndex === -1) {
        // Completed - all phases done
        status = "completed";
      } else if (phaseIndex < currentIndex) {
        status = "completed";
      } else if (phaseIndex === currentIndex) {
        status = "active";
      }
    } else if (!isProcessing && documents.length > 0) {
      // Not processing and has documents = completed
      status = "completed";
    }

    return { ...phase, status };
  });

  // Derive compliance status from server data or defaults
  const serverComplianceStatus = extractionMetadata?.complianceStatus;
  const derivedFinmaCompliant = serverComplianceStatus
    ? serverComplianceStatus === "finma_compliant" ||
      serverComplianceStatus === "finma_not_required"
    : (extractionMetadata?.finmaCompliant ?? true);

  // Default metadata if not provided
  const metadata: ExtractionMetadata = {
    modelUsed: extractionMetadata?.modelUsed || currentModel.name,
    modelVersion: extractionMetadata?.modelVersion || selectedModel,
    region: extractionMetadata?.region || "Zurich, Switzerland",
    regionCode: extractionMetadata?.regionCode || "europe-west6",
    datacenterLocation:
      extractionMetadata?.datacenterLocation || "Google Cloud Zurich",
    processingTimeMs: extractionMetadata?.processingTimeMs,
    tokensUsed: extractionMetadata?.tokensUsed,
    finmaCompliant: derivedFinmaCompliant,
    finmaChecks: extractionMetadata?.finmaChecks || DEFAULT_FINMA_CHECKS,
    complianceStatus: serverComplianceStatus,
    dataResidency: extractionMetadata?.dataResidency || "switzerland",
    encryptionStatus: extractionMetadata?.encryptionStatus || "both",
  };

  // Extract bank name from document classification subtype if available
  const getDocumentBank = (doc: SourceDocument) => {
    // The subtype often contains the bank name for bank statements
    if (
      doc.classification?.type === "bank_statement" &&
      doc.classification?.subtype
    ) {
      return doc.classification.subtype;
    }
    return undefined;
  };

  return (
    <div className="grid grid-cols-12 gap-4 h-full min-h-[500px]">
      {/* ========== LEFT PANEL: Process Flow ========== */}
      <div className="col-span-3">
        <Card className="h-full">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <Route className="h-4 w-4 text-primary" />
              Discovery Process
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 py-0">
            <div className="space-y-1">
              {phases.map((phase, index) => {
                const PhaseIcon = phase.icon;
                return (
                  <div key={phase.id}>
                    <div
                      className={cn(
                        "p-3 rounded-lg transition-all",
                        phase.status === "active" &&
                          "bg-primary/10 border border-primary/30",
                        phase.status === "completed" && "bg-success/5",
                        phase.status === "pending" && "opacity-50"
                      )}
                    >
                      <div className="flex items-start gap-2">
                        <div
                          className={cn(
                            "mt-0.5 flex-shrink-0 h-6 w-6 rounded-full flex items-center justify-center",
                            phase.status === "completed" &&
                              "bg-success text-[var(--success-foreground)]",
                            phase.status === "active" &&
                              "bg-primary text-primary-foreground",
                            phase.status === "pending" && "bg-muted"
                          )}
                        >
                          {phase.status === "completed" ? (
                            <Check className="h-3.5 w-3.5" />
                          ) : (
                            <PhaseIcon className="h-3.5 w-3.5" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">
                            {phase.label}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {phase.description}
                          </div>
                        </div>
                      </div>
                    </div>
                    {index < phases.length - 1 && (
                      <div className="flex justify-center py-0.5">
                        <ChevronRight
                          className={cn(
                            "h-4 w-4 rotate-90",
                            phase.status === "completed"
                              ? "text-success"
                              : "text-muted-foreground/30"
                          )}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Census-First Pipeline Badge */}
            <div className="mt-4 pt-3 border-t">
              <Badge
                variant="outline"
                className="w-full justify-center gap-1.5 py-1.5"
              >
                <Fingerprint className="h-3 w-3" />
                Census-First Pipeline
              </Badge>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Source-attributed extraction with full audit trail
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ========== MIDDLE PANEL: Documents & Work ========== */}
      <div className="col-span-5">
        <Card className="h-full flex flex-col">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-primary" />
                Processed Documents
              </span>
              <Badge variant="secondary">{documents.length} files</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 px-4 py-0 pb-4 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="space-y-2">
                {documents.map(doc => {
                  const bankName = getDocumentBank(doc);
                  const isSelected = selectedDocument?.id === doc.id;

                  return (
                    <button
                      key={doc.id}
                      onClick={() =>
                        setSelectedDocument(isSelected ? null : doc)
                      }
                      className={cn(
                        "w-full text-left p-3 rounded-lg border transition-all",
                        isSelected
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50 hover:bg-accent/50"
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={cn(
                            "p-2 rounded-lg",
                            doc.processingStatus === "completed"
                              ? "bg-success/10"
                              : "bg-muted"
                          )}
                        >
                          <FileText
                            className={cn(
                              "h-4 w-4",
                              doc.processingStatus === "completed"
                                ? "text-success"
                                : "text-muted-foreground"
                            )}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div
                            className="font-medium text-sm truncate"
                            title={doc.name}
                          >
                            {doc.name}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {doc.classification?.type && (
                              <Badge variant="secondary" className="text-xs">
                                {doc.classification.type.replace(/_/g, " ")}
                              </Badge>
                            )}
                            {bankName && (
                              <Badge variant="outline" className="text-xs">
                                {bankName}
                              </Badge>
                            )}
                          </div>
                        </div>
                        {doc.processingStatus === "completed" && (
                          <Check className="h-4 w-4 text-success flex-shrink-0" />
                        )}
                      </div>
                    </button>
                  );
                })}

                {documents.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No documents processed yet</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* ========== RIGHT PANEL: Metadata & Compliance ========== */}
      <div className="col-span-4">
        <Card className="h-full flex flex-col">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              Extraction Metadata & Compliance
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-1 px-4 py-0 pb-4 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="space-y-4">
                {/* Model Information */}
                <div>
                  <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                    <Brain className="h-3.5 w-3.5" />
                    AI Model
                  </div>
                  <div className="space-y-3">
                    {/* Model Selection */}
                    {canChangeModel && onModelChange ? (
                      <div className="space-y-2">
                        <Select
                          value={selectedModel}
                          onValueChange={value =>
                            onModelChange(value as ModelId)
                          }
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue>
                              {(() => {
                                const TierIcon = getTierIcon(currentModel.tier);
                                return (
                                  <div className="flex items-center gap-2">
                                    <TierIcon
                                      className={cn(
                                        "h-3.5 w-3.5",
                                        getTierColor(currentModel.tier)
                                      )}
                                    />
                                    <span>{currentModel.name}</span>
                                  </div>
                                );
                              })()}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {AVAILABLE_MODELS.map(model => {
                              const TierIcon = getTierIcon(model.tier);
                              return (
                                <SelectItem key={model.id} value={model.id}>
                                  <div className="flex items-center gap-2">
                                    <TierIcon
                                      className={cn(
                                        "h-3.5 w-3.5",
                                        getTierColor(model.tier)
                                      )}
                                    />
                                    <span className="font-medium">
                                      {model.name}
                                    </span>
                                    <span className="text-muted-foreground text-xs">
                                      ({model.fullName})
                                    </span>
                                    {model.tier === "standard" && (
                                      <Badge
                                        variant="secondary"
                                        className="text-xs ml-1"
                                      >
                                        Default
                                      </Badge>
                                    )}
                                  </div>
                                </SelectItem>
                              );
                            })}
                            {/* Custom model option */}
                            <SelectItem value="custom">
                              <div className="flex items-center gap-2">
                                <Settings className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="font-medium">Custom</span>
                                <span className="text-muted-foreground text-xs">
                                  (from AI Settings)
                                </span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          {currentModel.description}
                          {currentModel.tier === "custom" && (
                            <a
                              href="/ai-settings"
                              className="ml-1 text-primary hover:underline"
                            >
                              Configure →
                            </a>
                          )}
                        </p>
                      </div>
                    ) : (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">
                          Model
                        </span>
                        <div className="flex items-center gap-1.5">
                          {(() => {
                            const TierIcon = getTierIcon(currentModel.tier);
                            return (
                              <TierIcon
                                className={cn(
                                  "h-3.5 w-3.5",
                                  getTierColor(currentModel.tier)
                                )}
                              />
                            );
                          })()}
                          <span className="text-sm font-medium">
                            {currentModel.fullName || metadata.modelUsed}
                          </span>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Version
                      </span>
                      <span className="text-xs font-mono text-muted-foreground">
                        {metadata.modelVersion}
                      </span>
                    </div>
                    {metadata.tokensUsed && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">
                          Tokens Used
                        </span>
                        <span className="text-sm">
                          {metadata.tokensUsed.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Regional Location */}
                <div>
                  <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                    <Globe className="h-3.5 w-3.5" />
                    Data Location
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Region
                      </span>
                      <span className="text-sm font-medium">
                        {metadata.region}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Datacenter
                      </span>
                      <span className="text-sm">
                        {metadata.datacenterLocation}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Region Code
                      </span>
                      <Badge variant="outline" className="text-xs font-mono">
                        {metadata.regionCode}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Data Residency
                      </span>
                      <Badge
                        variant={
                          metadata.dataResidency === "switzerland"
                            ? "default"
                            : "secondary"
                        }
                        className="text-xs"
                      >
                        {metadata.dataResidency === "switzerland"
                          ? "🇨🇭 Switzerland"
                          : "🇪🇺 EU"}
                      </Badge>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Security Status */}
                <div>
                  <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                    <Lock className="h-3.5 w-3.5" />
                    Security
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Encryption
                      </span>
                      <Badge
                        variant={
                          metadata.encryptionStatus === "both"
                            ? "default"
                            : "secondary"
                        }
                        className="text-xs"
                      >
                        {metadata.encryptionStatus === "both"
                          ? "At Rest + In Transit"
                          : metadata.encryptionStatus}
                      </Badge>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* FINMA Compliance */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      <ShieldCheck className="h-3.5 w-3.5" />
                      FINMA Compliance
                    </div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge
                            variant={
                              metadata.complianceStatus ===
                              "non_finma_acknowledged"
                                ? "secondary"
                                : metadata.finmaCompliant
                                  ? "default"
                                  : "destructive"
                            }
                            className={`gap-1 ${
                              metadata.complianceStatus ===
                              "non_finma_acknowledged"
                                ? "bg-warning/15 text-warning"
                                : ""
                            }`}
                          >
                            {metadata.complianceStatus ===
                            "non_finma_acknowledged" ? (
                              <>
                                <AlertTriangle className="h-3 w-3" />
                                Acknowledged
                              </>
                            ) : metadata.finmaCompliant ? (
                              <>
                                <Check className="h-3 w-3" />
                                Compliant
                              </>
                            ) : (
                              <>
                                <AlertTriangle className="h-3 w-3" />
                                Review Required
                              </>
                            )}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            Swiss Financial Market Supervisory Authority
                            <br />
                            compliance status for data processing
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>

                  <div className="space-y-1.5">
                    {metadata.finmaChecks.map(check => (
                      <div
                        key={check.id}
                        className={cn(
                          "flex items-start gap-2 p-2 rounded-lg text-xs",
                          check.status === "pass" && "bg-success/5",
                          check.status === "fail" && "bg-destructive/5",
                          check.status === "warning" && "bg-warning/5",
                          check.status === "not_applicable" && "bg-muted/50"
                        )}
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          {check.status === "pass" && (
                            <Check className="h-3.5 w-3.5 text-success" />
                          )}
                          {check.status === "fail" && (
                            <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                          )}
                          {check.status === "warning" && (
                            <AlertTriangle className="h-3.5 w-3.5 text-warning" />
                          )}
                          {check.status === "not_applicable" && (
                            <Info className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">{check.label}</div>
                          <div className="text-muted-foreground">
                            {check.description}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* FINMA Info Link */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full gap-2 text-xs"
                  asChild
                >
                  <a
                    href="https://www.finma.ch/en/supervision/outsourcing/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="h-3 w-3" />
                    FINMA Outsourcing Guidelines
                  </a>
                </Button>
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default DiscoveryFlowPanel;
